import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IUrlParams } from './attributionService';
import { EBannerMode, ICookieConsent, IEventActionPayload, IMessage } from './types';

// Payload interfaces for complex actions
export interface IFirebaseInitPayload {
  apiKey: string;
  projectId: string;
  appId: string;
  messagingSenderId: string;
  measurementId: string;
  searchParams: URLSearchParams;
}

export interface IFirebaseStartPayload {
  analytics?: boolean;
  config?: boolean;
  messaging?: boolean;
}

export interface IFirebaseReadyPayload {
  analytics: boolean;
  config: boolean;
  messaging: boolean;
}

export interface IUpdateFirebaseConsentPayload {
  analytics: boolean;
  userData: boolean;
}

export interface ISetUserPayload {
  userId: number;
  email?: string;
}

export interface ISetPropertyPayload {
  name: string;
  value: string | number | boolean | undefined;
}

export interface IPushPermissionResultPayload {
  permission: boolean;
  reason?: string;
  unsupported?: boolean;
}

export interface IFirebaseState {
  initDone: boolean;
  // ready flag
  ready: boolean;
  // apps started in current session
  analytics: boolean;
  config: boolean;
  messaging: boolean;
  // list of messages received in foreground
  messages: IMessage[];
  // utm parameters found in url
  utm: IUrlParams;
  // consent for firebase given
  firebase: boolean;
  // consent for google userData given
  userData: boolean;
  // cookie banner mode
  mode: EBannerMode;
  // user choice is submitted
  submitted: boolean;
  // settings dialog open
  settings: boolean;
}

export const initialState: IFirebaseState = {
  initDone: false,
  ready: false,
  analytics: false,
  config: false,
  messaging: false,
  messages: [],
  utm: {},
  firebase: false,
  userData: false,
  // default banner mode changed to Postponable as requested in BW-4143
  mode: EBannerMode.Postponable,
  submitted: false,
  settings: false,
};

export const statePropName = 'firebase';

const firebaseSlice = createSlice({
  name: statePropName,
  initialState,
  reducers: {
    firebaseInit: {
      reducer: (state, action: PayloadAction<IFirebaseInitPayload>) => {
        return { ...initialState };
      },
      prepare: (
        apiKey: string,
        projectId: string,
        appId: string,
        messagingSenderId: string,
        measurementId: string,
        searchParams: URLSearchParams
      ) => ({
        payload: { apiKey, projectId, appId, messagingSenderId, measurementId, searchParams },
      }),
    },
    firebaseInitialized: (state) => {
      state.initDone = true;
    },
    firebaseStart: (state, action: PayloadAction<IFirebaseStartPayload>) => {
      // Action handled by saga
    },
    firebaseReady: {
      reducer: (state, action: PayloadAction<IFirebaseReadyPayload>) => {
        const { analytics, config, messaging } = action.payload;
        state.ready = true;
        state.analytics = analytics;
        state.config = config;
        state.messaging = messaging;
      },
      prepare: (analytics: boolean, config: boolean, messaging: boolean) => ({
        payload: { analytics, config, messaging },
      }),
    },
    updateFirebaseConsent: (state, action: PayloadAction<IUpdateFirebaseConsentPayload>) => {
      // Action handled by saga
    },
    analyticsEvent: (state, action: PayloadAction<IEventActionPayload>) => {
      // Action handled by saga
    },
    setUser: {
      reducer: (state, action: PayloadAction<ISetUserPayload>) => {
        // Action handled by saga
      },
      prepare: (userId: number, email?: string) => ({
        payload: { userId, email },
      }),
    },
    initTaboola: (state, action: PayloadAction<string | undefined>) => {
      // Action handled by saga
    },
    taboolaSubscribeEvent: (state) => {
      // Action handled by saga
    },
    taboolaValidationEvent: (state) => {
      // Action handled by saga
    },
    initOutbrain: (state, action: PayloadAction<string | undefined>) => {
      // Action handled by saga
    },
    outbrainSubscribeEvent: (state) => {
      // Action handled by saga
    },
    outbrainValidationEvent: (state) => {
      // Action handled by saga
    },
    setUserProperty: {
      reducer: (state, action: PayloadAction<ISetPropertyPayload>) => {
        // Action handled by saga
      },
      prepare: (name: string, value: string | number | boolean | undefined) => ({
        payload: { name, value },
      }),
    },
    requestPushPermission: (state, action: PayloadAction<string>) => {
      // Action handled by saga
    },
    pushPermissionResult: {
      reducer: (state, action: PayloadAction<IPushPermissionResultPayload>) => {
        // Action handled by saga
      },
      prepare: (permission: boolean, reason?: string, unsupported?: boolean) => ({
        payload: { permission, reason, unsupported },
      }),
    },
    enableMessaging: (state, action: PayloadAction<string>) => {
      // Action handled by saga
    },
    messagingEnabled: (state) => {
      state.messaging = true;
    },
    messageReceived: (state, action: PayloadAction<IMessage>) => {
      state.messages.push(action.payload);
    },
    messageShown: (state, action: PayloadAction<IMessage>) => {
      state.messages = state.messages.filter((m) => m.messageId !== action.payload.messageId);
    },
    messageClicked: (state, action: PayloadAction<string>) => {
      // Action handled by saga
    },
    updateUtmTracking: (state, action: PayloadAction<IUrlParams>) => {
      state.utm = action.payload;
    },
    updateBannerMode: (state, action: PayloadAction<EBannerMode>) => {
      state.mode = action.payload;
    },
    updateConsent: (state, action: PayloadAction<ICookieConsent>) => {
      state.firebase = !!action.payload.firebase;
      state.userData = !!action.payload.userData;
      state.submitted = true;
      state.settings = false;
    },
    saveConsent: (state, action: PayloadAction<ICookieConsent>) => {
      // Action handled by saga
    },
    toggleSettings: (state) => {
      state.settings = !state.settings;
    },
    initGdprExport: (state) => {
      // Action handled by saga
    },
    requestExport: (state) => {
      // Action handled by saga
    },
    requestExportSuccess: (state) => {
      // Action handled by saga
    },
    requestExportFailure: (state) => {
      // Action handled by saga
    },
  },
});

// Helper action creators that use existing actions
export const firebaseErrorReport = (page: string, feature: string, message: string) => {
  const event: IEventActionPayload = {
    eventName: 'Error_frontend',
    parameters: [
      {
        key: 'Page',
        value: page,
      },
      {
        key: 'Feature',
        value: feature,
      },
      {
        key: 'Message',
        value: message,
      },
    ],
  };
  return FirebaseActions.analyticsEvent(event);
};

export const acceptAll = () => {
  const consent: ICookieConsent = {
    firebase: true,
    userData: true,
  };
  return FirebaseActions.saveConsent(consent);
};

export const declineAll = () => {
  const consent: ICookieConsent = {
    firebase: false,
    userData: false,
  };
  return FirebaseActions.saveConsent(consent);
};

// Export the action creators
export const FirebaseActions = { ...firebaseSlice.actions, firebaseErrorReport, acceptAll, declineAll };

// Export the reducer
export default firebaseSlice.reducer;
